import { PROFILE, SKILLS, CINEMAS_SPINE_URL } from "@/data/content";
import { Reveal, MaskedLines } from "@/components/bits/Reveal";
import { Sticker, Tape, HandNote, Arrow } from "@/components/bits/Sticker";

export const About = () => (
  <section id="about" className="relative max-w-6xl mx-auto px-6 md:px-10 py-24 md:py-36" data-testid="about-section">
    <Reveal className="flex items-end gap-6 mb-14">
      <h2 className="pixel-title text-5xl sm:text-6xl md:text-8xl">About</h2>
      <HandNote className="text-2xl md:text-3xl -rotate-3 mb-2">me !</HandNote>
    </Reveal>

    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
      <Reveal className="lg:col-span-4 relative" delay={0.1}>
        <div className="polaroid max-w-[280px] mx-auto lg:mx-0" style={{ "--r": "-3deg" }}>
          <Tape rotate={-8} className="-top-3 left-6" color="blue" />
          <Tape rotate={10} className="-top-2 right-4 !w-16" color="pink" />
          <img src={PROFILE.photo} alt="Anushka Purohit portrait" data-testid="about-portrait" className="!aspect-[3/4]" />
          <HandNote className="absolute bottom-2 left-4 text-xl">me, probably between takes</HandNote>
        </div>
        <div className="mt-8 flex flex-col gap-2 items-start">
          {[["Based in", PROFILE.location, "yellow"], ["Pronouns", PROFILE.pronouns, "pink"], ["Years on set", `${PROFILE.years}+`, "green"]].map(([k, v, c]) => (
            <Sticker key={k} color={c} rotate={-1} className="!normal-case !tracking-normal">
              <span className="opacity-60">{k}</span> · {v}
            </Sticker>
          ))}
        </div>
      </Reveal>

      <div className="lg:col-span-8 relative">
        <Reveal className="inline-block relative mb-6">
          <span className="inline-block bg-white border-[1.5px] border-[var(--ink)] px-4 py-1.5 font-mono text-xs tracking-[0.15em] uppercase hard-shadow">In a Nutshell</span>
          <Arrow className="absolute -right-20 -top-4 w-16 text-[var(--ink-60)] rotate-[30deg] hidden md:block" />
        </Reveal>

        <MaskedLines
          inView
          className="font-hand text-2xl sm:text-3xl md:text-[2.4rem] leading-[1.15] text-[var(--ink)]"
          stagger={0.08}
          lines={[
            <>A little about what I do and why.</>,
            <>Nine years in, I’ve worked across almost</>,
            <>every stage of bringing a story to life —</>,
            <>from the first idea to the final frame.</>,
          ]}
        />

        <Reveal className="mt-8 font-body text-base md:text-lg text-[var(--ink-60)] max-w-2xl leading-relaxed" delay={0.2}>
          <p>
            Somewhere along the way, I realised I’m most interested in what makes the whole thing work: the people, the details, the decisions, and all the things that have to fall into place before a story can really take shape.
          </p>
          <p className="mt-4">
            Curiosity, attention to detail and a practical streak tend to shape the way I work. Mindful of the budget, meticulous about the details, and always interested in finding a better way through.
          </p>
          <p className="mt-4">
            Beyond production, I’m interested in analysing, researching and developing scripts, sitting in on brainstorms, and collecting the stories and insights that live behind the work. That curiosity once led me to develop <a href={CINEMAS_SPINE_URL} target="_blank" rel="noopener noreferrer" className="text-[var(--ink)] underline underline-offset-2">Cinema’s Spine</a>, a podcast about the people and processes behind what we see on screen.
          </p>
        </Reveal>

        <Reveal className="mt-10" delay={0.25}>
          <h3 className="font-mono text-xs tracking-[0.15em] uppercase text-[var(--ink)] mb-4">My work &amp; interests span:</h3>
          <div className="flex flex-wrap gap-3" data-testid="skills-list">
            {SKILLS.map((s, i) => (
              <Sticker key={s.label} color={s.color} rotate={i % 2 ? 1.5 : -1.5} data-testid={`skill-${i}`}>
                {s.label}
              </Sticker>
            ))}
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);
